import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, User, Layers,
  TrendingUp, TrendingDown, Minus,
  Film, FileText, Book, Tv,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import UpgradeModal from '../components/UpgradeModal';
import { useProject } from '../context/ProjectContext';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { workspaceRoute } from '../lib/workspaceRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARC_TYPES = [
  {
    id: 'positive',
    name: 'Positive Change',
    icon: TrendingUp,
    description: 'Character overcomes their Lie and embraces the Truth',
    color: 'text-green-500',
  },
  {
    id: 'negative',
    name: 'Negative Change',
    icon: TrendingDown,
    description: 'Character succumbs to corruption or falls from grace',
    color: 'text-red-500',
  },
  {
    id: 'flat',
    name: 'Flat Arc',
    icon: Minus,
    description: 'Character holds the Truth and changes the world around them',
    color: 'text-blue-500',
  },
];

const FORMATS = [
  { id: 'feature',    name: 'Feature Film', icon: Film,     description: '90-120 minutes. 15-beat structure. Full character depth and subplots.' },
  { id: 'short',      name: 'Short Film',   icon: FileText, description: '5-30 minutes. 7-beat structure. One character, one problem, one change.' },
  { id: 'tv_episode', name: 'TV Episode',   icon: Tv,       description: '22-60 minutes. 10-beat structure. A-story, B-story, season thread.' },
  { id: 'tv_pilot',   name: 'Pilot',        icon: Tv,       description: '12-beat structure. Establish the world, the conflict, and the hook.' },
];

const ROLES = [
  { id: 'protagonist', label: 'Protagonist' },
  { id: 'antagonist',  label: 'Antagonist' },
  { id: 'supporting',  label: 'Supporting' },
];

export default function CreateProject() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { project: activeProject } = useProject();

  const projectType = searchParams.get('type') || 'character';
  const workspace = searchParams.get('workspace') || null;

  // The workspace project this character belongs to. We fetch it so we know
  // (a) its title for grouping and (b) whether it's a series — series
  // workspaces live at /series/:id, not /project/:id.
  const [wsProject, setWsProject] = useState(null);
  useEffect(() => {
    if (!workspace) return;
    authFetch(`${API}/projects/${workspace}`, { credentials: 'include' })
      .then((r) => r.json())
      .then(setWsProject)
      .catch(() => {});
  }, [workspace]);

  // In workspace character mode, title is silently pulled from context, with
  // the fetched workspace project as a fallback (SeriesBuilder doesn't seed
  // the context the way ProjectWorkspace does).
  const workspaceTitle = activeProject?.projectTitle || wsProject?.title || '';
  const isWorkspaceChar = Boolean(workspace && projectType === 'character');

  // ── Standard (non-workspace) state ──
  const [title, setTitle] = useState('');
  const [selectedFormat, setSelectedFormat] = useState(null);

  // ── Workspace character state ──
  const [step, setStep] = useState(1); // 1 = arc picker, 2 = character details
  const [selectedArc, setSelectedArc] = useState(null);
  const [selectedRole, setSelectedRole] = useState('supporting');
  const [charDetails, setCharDetails] = useState({ name: '', age: '', gender: '', ethnicity: '', biography: '' });

  const [loading, setLoading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleStandardSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Please enter a project title'); return; }
    if (projectType === 'character' && !selectedArc) { toast.error('Please select an arc type'); return; }
    if (projectType === 'plot' && !selectedFormat) { toast.error('Please select a format'); return; }

    setLoading(true);
    try {
      const endpoint = projectType === 'character' ? `${API}/projects/character` : `${API}/projects/plot`;
      const body = projectType === 'character'
        ? { title, arc_type: selectedArc, character_role: 'protagonist' }
        : { title, format: selectedFormat };

      const res = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.status === 403) { setShowLimitModal(true); return; }
      if (!res.ok) throw new Error('Failed to create project');

      const project = await res.json();
      toast.success('Project created!');
      navigate(projectType === 'character'
        ? `/character/${project.project_id}/level/0`
        : `/project/${project.project_id}`);
    } catch {
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceSubmit = async (e) => {
    e.preventDefault();
    if (!charDetails.name.trim()) { toast.error('Please enter a character name'); return; }

    setLoading(true);
    try {
      // 1. Create the project (title = workspace project title for grouping)
      const res = await authFetch(`${API}/projects/character`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: workspaceTitle,
          arc_type: selectedArc,
          character_role: selectedRole,
          workspace_id: workspace,
        }),
      });
      if (res.status === 403) { setShowLimitModal(true); return; }
      if (!res.ok) throw new Error('Failed to create project');

      const project = await res.json();

      // 2. Save level_0 details immediately
      const hasDetails = Object.values(charDetails).some(v => v.trim());
      if (hasDetails) {
        await authFetch(`${API}/projects/${project.project_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ level: 0, data: charDetails, completed: false }),
        });
      }

      toast.success('Character created!');
      // Resolve the workspace format (await a fetch if the on-mount one
      // hasn't resolved yet) so series characters return to the Series
      // Builder rather than the film workspace.
      let wsFormat = wsProject?.format;
      if (wsFormat === undefined) {
        try {
          const wp = await authFetch(`${API}/projects/${workspace}`, { credentials: 'include' }).then((r) => r.json());
          wsFormat = wp?.format;
        } catch { /* fall back to default /project route */ }
      }
      navigate(workspaceRoute(workspace, wsFormat, 'characters'));
    } catch {
      toast.error('Failed to create character');
    } finally {
      setLoading(false);
    }
  };

  const backTarget = workspace
    ? workspaceRoute(workspace, wsProject?.format, 'characters')
    : `/dashboard?type=${projectType}`;

  // ── WORKSPACE CHARACTER FLOW ───────────────────────────────────────────────
  if (isWorkspaceChar) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="noise-overlay" />

        <header className="glassmorphism sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => step === 2 ? setStep(1) : navigate(backTarget)}
              className="rounded-none font-mono uppercase tracking-widest text-xs"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {step === 2 ? 'Back' : 'Back to Workspace'}
            </Button>
            {/* Step indicator */}
            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <span className={step === 1 ? 'text-primary' : ''}>1. Arc</span>
              <span className="text-border">—</span>
              <span className={step === 2 ? 'text-primary' : ''}>2. Details</span>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Step 1: Arc Type */}
          {step === 1 && (
            <>
              <div className="mb-10 text-center">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                  {workspaceTitle}
                </p>
                <h1 className="font-mono text-3xl font-semibold tracking-tight mb-3">
                  What kind of journey will this character take?
                </h1>
                <p className="text-muted-foreground text-sm">
                  This shapes which blueprint questions they'll answer.
                </p>
              </div>

              <div className="space-y-4 mb-10">
                {ARC_TYPES.map((arc) => (
                  <div
                    key={arc.id}
                    onClick={() => setSelectedArc(arc.id)}
                    data-testid={`arc-type-${arc.id}`}
                    className={`bg-card border p-6 cursor-pointer transition-all duration-200
                      ${selectedArc === arc.id
                        ? 'border-primary neon-border'
                        : 'border-border/40 hover:border-primary/30'
                      }`}
                  >
                    <div className="flex items-start gap-4">
                      <arc.icon className={`w-8 h-8 ${arc.color} flex-shrink-0`} />
                      <div className="flex-1">
                        <h3 className="font-mono text-lg font-semibold mb-1">{arc.name}</h3>
                        <p className="text-muted-foreground text-sm">{arc.description}</p>
                      </div>
                      {selectedArc === arc.id && (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => {
                  if (!selectedArc) { toast.error('Pick an arc type to continue'); return; }
                  setStep(2);
                }}
                data-testid="arc-continue-btn"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs py-6 shadow-glow"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {/* Step 2: Character Details */}
          {step === 2 && (
            <>
              <div className="mb-10 text-center">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                  {workspaceTitle} · {ARC_TYPES.find(a => a.id === selectedArc)?.name}
                </p>
                <h1 className="font-mono text-3xl font-semibold tracking-tight mb-3">
                  Tell us about this character.
                </h1>
              </div>

              <form onSubmit={handleWorkspaceSubmit} className="space-y-6">
                {/* Role */}
                <div className="space-y-3">
                  <label className="block font-mono text-sm uppercase tracking-wider text-primary">
                    Role
                  </label>
                  <div className="flex gap-3">
                    {ROLES.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setSelectedRole(r.id)}
                        data-testid={`role-${r.id}`}
                        className={`flex-1 py-3 font-mono text-xs uppercase tracking-widest border transition-all
                          ${selectedRole === r.id
                            ? 'bg-primary/20 border-primary text-primary'
                            : 'border-border/40 text-muted-foreground hover:border-border hover:text-foreground'
                          }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <label className="block font-mono text-sm uppercase tracking-wider text-foreground">
                    Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    value={charDetails.name}
                    onChange={(e) => setCharDetails(p => ({ ...p, name: e.target.value }))}
                    placeholder="Character full name"
                    data-testid="char-name-input"
                    className="bg-background border-input rounded-none font-mono py-5"
                  />
                </div>

                {/* Age & Gender */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-mono text-sm uppercase tracking-wider text-foreground">Age</label>
                    <Input
                      value={charDetails.age}
                      onChange={(e) => setCharDetails(p => ({ ...p, age: e.target.value }))}
                      placeholder="e.g. 28"
                      className="bg-background border-input rounded-none font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-mono text-sm uppercase tracking-wider text-foreground">Gender</label>
                    <Input
                      value={charDetails.gender}
                      onChange={(e) => setCharDetails(p => ({ ...p, gender: e.target.value }))}
                      placeholder="Their gender identity"
                      className="bg-background border-input rounded-none font-mono"
                    />
                  </div>
                </div>

                {/* Ethnicity */}
                <div className="space-y-2">
                  <label className="block font-mono text-sm uppercase tracking-wider text-foreground">Ethnicity</label>
                  <Input
                    value={charDetails.ethnicity}
                    onChange={(e) => setCharDetails(p => ({ ...p, ethnicity: e.target.value }))}
                    placeholder="Cultural background"
                    className="bg-background border-input rounded-none font-mono"
                  />
                </div>

                {/* Biography */}
                <div className="space-y-2">
                  <label className="block font-mono text-sm uppercase tracking-wider text-foreground">Short Biography</label>
                  <AutoExpandTextarea
                    value={charDetails.biography}
                    onChange={(e) => setCharDetails(p => ({ ...p, biography: e.target.value }))}
                    placeholder="A brief overview of their life — Who, What, When, Where, Why"
                    className="bg-background border-input rounded-none font-mono min-h-[100px]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-project-btn"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs py-6 shadow-glow"
                >
                  {loading ? 'Creating...' : 'Create Character'}
                </Button>
              </form>
            </>
          )}
        </main>

        {showLimitModal && (
          <UpgradeModal
            title="Your story needs a full cast."
            description="You've built your Protagonist and Antagonist. Upgrade to add supporting characters, love interests, and more."
            benefits={[
              'Up to 3 projects on Indie',
              'Unlimited projects on Studio',
              'Full World Building suite',
              'Clean, watermark-free exports',
            ]}
            ctaText="Expand Your Library"
            onClose={() => setShowLimitModal(false)}
          />
        )}
      </div>
    );
  }

  // ── STANDARD FLOW (no workspace param) ────────────────────────────────────
  const Icon = projectType === 'character' ? User : Layers;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(backTarget)}
            data-testid="back-btn"
            className="rounded-none font-mono uppercase tracking-widest text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-center">
          <Icon className="w-16 h-16 text-primary mx-auto mb-6" />
          <h1
            className="font-mono text-3xl md:text-4xl font-semibold tracking-tight mb-4"
            data-testid="create-project-heading"
          >
            Create {projectType === 'character' ? 'Character' : 'Plot'} Project
          </h1>
          <p className="text-muted-foreground text-lg">
            {projectType === 'character'
              ? "Choose your character's journey type to begin building depth."
              : 'Select your format to structure your story beats.'}
          </p>
        </div>

        <form onSubmit={handleStandardSubmit} className="space-y-8">
          {/* Project Title */}
          <div className="space-y-3">
            <label htmlFor="title" className="block font-mono text-sm uppercase tracking-wider text-foreground">
              Project Title
            </label>
            <Input
              id="title"
              type="text"
              placeholder={projectType === 'character' ? 'The Fallen Knight' : 'My Feature Script'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="project-title-input"
              className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50 text-lg py-6"
            />
          </div>

          {projectType === 'character' ? (
            <div className="space-y-4">
              <label className="block font-mono text-sm uppercase tracking-wider text-foreground">
                Character Arc Type
              </label>
              <div className="grid grid-cols-1 gap-4">
                {ARC_TYPES.map((arc) => (
                  <div
                    key={arc.id}
                    onClick={() => setSelectedArc(arc.id)}
                    data-testid={`arc-type-${arc.id}`}
                    className={`bg-card border ${
                      selectedArc === arc.id ? 'border-primary neon-border' : 'border-border/40 hover:border-primary/30'
                    } p-6 cursor-pointer transition-all duration-300`}
                  >
                    <div className="flex items-start gap-4">
                      <arc.icon className={`w-8 h-8 ${arc.color} flex-shrink-0`} />
                      <div className="flex-1">
                        <h3 className="font-mono text-lg font-semibold mb-2">{arc.name}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{arc.description}</p>
                      </div>
                      {selectedArc === arc.id && (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <label className="block font-mono text-sm uppercase tracking-wider text-foreground">
                Story Format
              </label>
              <div className="grid grid-cols-1 gap-4">
                {FORMATS.map((format) => (
                  <div
                    key={format.id}
                    onClick={() => setSelectedFormat(format.id)}
                    data-testid={`format-${format.id}`}
                    className={`bg-card border ${
                      selectedFormat === format.id ? 'border-primary neon-border' : 'border-border/40 hover:border-primary/30'
                    } p-6 cursor-pointer transition-all duration-300`}
                  >
                    <div className="flex items-start gap-4">
                      <format.icon className="w-8 h-8 text-primary flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-mono text-lg font-semibold mb-2">{format.name}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{format.description}</p>
                      </div>
                      {selectedFormat === format.id && (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-project-btn"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-8 py-6 shadow-glow transition-all hover:shadow-glow-strong disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Project'}
          </Button>
        </form>
      </main>

      {showLimitModal && (
        <UpgradeModal
          title="Your story needs a full cast."
          description="You've built your Protagonist and Antagonist. Upgrade to add supporting characters, love interests, and more."
          benefits={[
            'Up to 3 projects on Indie',
            'Unlimited projects on Studio',
            'Full World Building suite',
            'Clean, watermark-free exports',
          ]}
          ctaText="Expand Your Library"
          onClose={() => setShowLimitModal(false)}
        />
      )}
    </div>
  );
}
